import Experience from "@folio-2026/experience"
import { EVENTS, createDebouncedHandler } from "@folio-2026/shared"
import { TextScene } from "@folio-2026/text"

// Constants for text search patterns
const SEARCH_TEXT_SOUND = "Sound:"
const SEARCH_TEXT_EMAIL = "hi@giulio.me"
const APP_FRAME_EVENT_DEBOUNCE_MS = 100

export default class AppFrame {
  static audioToggleListenerSetup = false
  static emailClickListenerSetup = false
  static emailHoverListenerSetup = false
  static menuEventListenersSetup = false
  static appFrameInstances: Map<string, AppFrame> = new Map()
  static currentlyHoveredText: TextScene["texts"][number] | null = null

  name: string
  experience: Experience
  sizes: Experience["sizes"]
  textScene: TextScene
  texts: TextScene["texts"] = []

  constructor({ name, textScene }: { name: string; textScene: TextScene }) {
    this.name = name
    this.experience = Experience.getInstance()
    this.sizes = this.experience.sizes

    this.textScene = textScene

    // Register this instance
    AppFrame.appFrameInstances.set(name, this)
  }

  init() {
    this.textScene.createTexts("app-frame")

    this.texts = this.textScene.texts.filter(
      (text) => text.name === "app-frame",
    )

    this.texts.forEach((text) => {
      this.setPosition(text)

      text.noiseDebugParams.noiseUvAmplitude.value = 0
      text.noiseDebugParams.noiseUvFrequency.value = 0
      // text.setupEventListeners()
      // text.reveal()
    })

    this.setEventListeners()
  }

  setEventListeners() {
    // Set up menu event listeners only once globally
    if (!AppFrame.menuEventListenersSetup) {
      AppFrame.menuEventListenersSetup = true
      const MENU_EVENT_DEBOUNCE_MS = 100
      const experience = Experience.getInstance()

      // Create separate debounced handlers, each with its own debounce state
      const debouncedClick = createDebouncedHandler(
        AppFrame.handleMenuItemClick,
        MENU_EVENT_DEBOUNCE_MS,
      )

      const debouncedHover = createDebouncedHandler(
        AppFrame.handleMenuItemHover,
        MENU_EVENT_DEBOUNCE_MS,
      )

      experience.on(EVENTS.Menu.click, () => {
        debouncedClick()
      })

      experience.on(EVENTS.Menu.hover, (e: unknown) => {
        const textInstance = AppFrame.extractTextInstance(e)
        if (!textInstance) {
          return
        }
        // Immediately clear any previously hovered text BEFORE debouncing
        // This ensures fast mouse movements don't leave items stuck in hover state
        // We do this synchronously to avoid any timing issues
        if (
          AppFrame.currentlyHoveredText &&
          AppFrame.currentlyHoveredText !== textInstance
        ) {
          // Clear hover state immediately, bypassing debounce
          AppFrame.currentlyHoveredText.hover(false)
          AppFrame.currentlyHoveredText = null
        }
        // Update tracked hover state immediately
        AppFrame.currentlyHoveredText = textInstance
        // Then call debounced handler (which will call onMenuItemHover)
        debouncedHover(textInstance)
      })

      experience.on(EVENTS.Menu.out, (e: unknown) => {
        const textInstance = AppFrame.extractTextInstance(e)
        if (textInstance) {
          // Don't debounce out events - they need to fire immediately
          // The hover handler already clears previous hovers, so this is just a backup
          AppFrame.handleMenuItemOut(textInstance)
        }
      })
    }

    // Reveal texts when the section is entered
    // EVENTS.Loader.AllSectionsUpdated -> Curtains start disappearing
    this.experience.on(EVENTS.Loader.CurtainStartedRemoving, () => {
      if (window.location.pathname !== "/") {
        return
      }

      for (let i = 0; i < this.texts.length; i++) {
        setTimeout(
          () => {
            requestAnimationFrame(() => {
              const currentText = this.texts[i]

              this.setPosition(currentText)
              currentText.reveal()
              currentText.setupEventListeners()
            })
          },
          1000 + i * 300,
        )
      }
    })

    // About page
    this.experience.on(EVENTS.About.Entered, () => {
      this.texts.forEach((text) => {
        text.leave()
      })
    })
    this.experience.on(EVENTS.About.Exiting, () => {
      this.texts.forEach((text) => {
        text.reveal()
      })
    })

    // Project page
    this.experience.on(EVENTS.Project.Entered, () => {
      this.texts.forEach((text) => {
        text.leave()
      })
    })
    this.experience.on(EVENTS.Project.Exiting, () => {
      this.texts.forEach((text) => {
        text.reveal()
      })
    })

    // Set up audio toggle listener only once globally
    if (!AppFrame.audioToggleListenerSetup) {
      AppFrame.audioToggleListenerSetup = true
      const experience = Experience.getInstance()

      const debouncedAudioToggle = createDebouncedHandler(
        AppFrame.handleAudioToggle,
        APP_FRAME_EVENT_DEBOUNCE_MS,
      )

      experience.on(EVENTS.Audio.Toggle, () => {
        debouncedAudioToggle()
      })
    }

    // Set up email click listener only once globally
    if (!AppFrame.emailClickListenerSetup) {
      AppFrame.emailClickListenerSetup = true
      const experience = Experience.getInstance()

      const debouncedEmailClick = createDebouncedHandler(
        AppFrame.handleEmailClick,
        APP_FRAME_EVENT_DEBOUNCE_MS,
      )

      experience.on(EVENTS.Email.Click, () => {
        debouncedEmailClick()
      })
    }

    // Set up email hover/out listeners only once globally
    if (!AppFrame.emailHoverListenerSetup) {
      AppFrame.emailHoverListenerSetup = true
      const experience = Experience.getInstance()

      const debouncedEmailHover = createDebouncedHandler(
        AppFrame.handleEmailHover,
        APP_FRAME_EVENT_DEBOUNCE_MS,
      )

      experience.on(EVENTS.Email.Hover, (e: unknown) => {
        const textInstance = AppFrame.extractTextInstance(e)
        if (textInstance) {
          debouncedEmailHover(textInstance)
        }
      })

      experience.on(EVENTS.Email.Out, (e: unknown) => {
        const textInstance = AppFrame.extractTextInstance(e)
        if (textInstance) {
          // Don't debounce out events - they need to fire immediately
          AppFrame.handleEmailOut(textInstance)
        }
      })
    }
  }

  // Static handlers that route to the current section's AppFrame instance
  static handleMenuItemClick() {
    const currentAppFrame = AppFrame.getCurrentAppFrame()
    if (currentAppFrame) {
      currentAppFrame.onMenuItemClick()
    }
  }

  static handleMenuItemHover(text: TextScene["texts"][number]) {
    const currentAppFrame = AppFrame.getCurrentAppFrame()
    if (currentAppFrame) {
      currentAppFrame.onMenuItemHover(text)
    }
  }

  static handleMenuItemOut(text: TextScene["texts"][number]) {
    const currentAppFrame = AppFrame.getCurrentAppFrame()
    if (currentAppFrame) {
      currentAppFrame.onMenuItemOut(text)
    }
  }

  static handleAudioToggle() {
    const experience = Experience.getInstance()

    // Emit the SetVolume event only once
    const currentSectionName = experience.scrollInfo.currentSection?.name

    if (!currentSectionName) {
      return
    }

    const currentAppFrame = AppFrame.appFrameInstances.get(currentSectionName)
    if (!currentAppFrame) {
      return
    }

    // Toggle the volume first
    experience.volume = experience.volume === 0 ? 1 : 0
    // Also update previousVolume so that updateVolume() doesn't restore the old value
    experience.previousVolume = experience.volume

    // Update the text of all AppFrame instances based on the new volume
    const newText = `${SEARCH_TEXT_SOUND} ${experience.volume === 0 ? "Off" : "On"}`

    // Iterate through all AppFrame instances and update their sound text
    AppFrame.appFrameInstances.forEach((appFrame) => {
      const soundText = appFrame.texts.find((text) =>
        text.text.startsWith(SEARCH_TEXT_SOUND),
      )

      if (soundText) {
        AppFrame.updateTextContent(soundText, newText)
      }
    })

    experience.trigger(EVENTS.Audio.SetVolume, [{ volume: experience.volume }])
  }

  static handleEmailClick() {
    window.open(`mailto:${SEARCH_TEXT_EMAIL}`, "_blank")
  }

  static handleEmailHover(text: TextScene["texts"][number]) {
    AppFrame.handleSimpleHover(text)
  }

  static handleEmailOut(text: TextScene["texts"][number]) {
    AppFrame.handleSimpleOut(text)
  }

  // Helper method to find text by content across all AppFrame instances
  static findTextByContent(
    searchText: string,
    matchType: "startsWith" | "includes" = "startsWith",
  ): TextScene["texts"][number] | null {
    for (const appFrame of AppFrame.appFrameInstances.values()) {
      const foundText = appFrame.texts.find((text) => {
        if (matchType === "startsWith") {
          return text.text.startsWith(searchText)
        }
        return text.text.includes(searchText)
      })
      if (foundText) {
        return foundText
      }
    }
    return null
  }

  // Helper method to update text content and geometry
  static updateTextContent(
    textInstance: TextScene["texts"][number],
    newContent: string,
  ) {
    textInstance.text = newContent
    textInstance.geometry.update({
      text: textInstance.text,
    })
  }

  // Helper method to get current AppFrame instance
  static getCurrentAppFrame(): AppFrame | null {
    const experience = Experience.getInstance()
    const currentSectionName = experience.scrollInfo.currentSection?.name

    if (!currentSectionName) {
      return null
    }

    return AppFrame.appFrameInstances.get(currentSectionName) || null
  }

  // Helper method to extract textInstance from event
  static extractTextInstance(e: unknown): TextScene["texts"][number] | null {
    const { textInstance } = e as {
      textInstance: TextScene["texts"][number]
    }
    return textInstance || null
  }

  // Simple hover handler for text elements
  static handleSimpleHover(text: TextScene["texts"][number]) {
    text.hover(true)
  }

  // Simple out handler for text elements
  static handleSimpleOut(text: TextScene["texts"][number]) {
    text.hover(false)
  }

  onMenuItemHover(text: TextScene["texts"][number]) {
    AppFrame.currentlyHoveredText = text
    text.hover(true)
  }

  onMenuItemOut(text: TextScene["texts"][number]) {
    if (AppFrame.currentlyHoveredText === text) {
      AppFrame.currentlyHoveredText = null
      text.hover(false)
    }
  }

  onMenuItemClick() {
    this.experience.isMenuOpen = !this.experience.isMenuOpen

    if (this.experience.isMenuOpen) {
      this.experience.lenis?.stop()
      this.experience.trigger(EVENTS.Menu.open)
    } else {
      this.experience.lenis?.start()
      this.experience.trigger(EVENTS.Menu.close)
    }
  }

  setPosition(text: TextScene["texts"][number]) {
    text.y = text.bounds.top
    const positionY = -text.y + this.sizes.height / 2 - text.bounds.height

    const positionX = text.bounds.left - this.sizes.width / 2

    text.mesh.position.y = positionY
    text.mesh.position.x = positionX

    text.proxyMesh.position.y = positionY
    text.proxyMesh.position.x = positionX
  }

  resize() {
    this.texts.forEach((text) => {
      this.setPosition(text)
    })
  }
}
